import { useEffect, useRef, useState } from 'react';

/**
 * True once the element comes within `margin` of the viewport, and stays true.
 * Lets below-the-fold content load just before it is seen, so visitors who
 * never scroll there cost nothing. Without IntersectionObserver it is true at once.
 */
export function useNearViewport<T extends Element>(margin = '600px 0px') {
  const ref = useRef<T>(null);
  const [near, setNear] = useState(() => typeof IntersectionObserver === 'undefined');

  useEffect(() => {
    const element = ref.current;
    if (near || !element) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setNear(true);
          observer.disconnect();
        }
      },
      { rootMargin: margin },
    );
    observer.observe(element);
    return () => observer.disconnect();
  }, [near, margin]);

  return [ref, near] as const;
}
